// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Switch.tsx
 * @input Uses React, useId, ChangeEvent, FieldLabel, FieldStatus, IconType, InputStatus, useTooltip
 * @output Exports Switch component, SwitchProps, SwitchLabelPosition, SwitchLabelSpacing
 * @position Core implementation; consumed by index.ts, tested by Switch.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Switch/Switch.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Switch/Switch.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Switch/index.ts (exports if types change)
 * - /apps/storybook/stories/Switch.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Switch/ (showcase blocks)
 */
import { useId, useOptimistic, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, typographyVars, typeScaleVars, focusVars } from "../theme/tokens.stylex.js";
import { FieldLabel } from "../Field/FieldLabel.js";
import { FieldStatus } from "../FieldStatus/FieldStatus.js";
import { Spinner } from "../Spinner/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import "./switch.markers.stylex.js";
import { switchScope } from "./switch.markers.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const wrapperSizeStyles = {
  sm: {
    kzqmXN: "x1td3qas",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  md: {
    kzqmXN: "x100vrsf",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};
const inputSizeStyles = {
  sm: {
    kzqmXN: "x1td3qas",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  md: {
    kzqmXN: "x100vrsf",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};
const trackSizeStyles = {
  sm: {
    kzqmXN: "x1td3qas",
    kZKoxP: "x1qx5ct2",
    kmVPX3: "x1i3ajwb",
    $$css: true
  },
  md: {
    kzqmXN: "x100vrsf",
    kZKoxP: "xxk0z11",
    kmVPX3: "xfawy5m",
    $$css: true
  }
};
const thumbOffSizeStyles = {
  sm: {
    kzqmXN: "x6jxa94",
    kZKoxP: "x1v9usgg",
    k3aq6I: "xbryuvx",
    $$css: true
  },
  md: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    k3aq6I: "xbryuvx",
    $$css: true
  }
};
const thumbOnSizeStyles = {
  sm: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    k3aq6I: "xv5t66b xi9aj42",
    $$css: true
  },
  md: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    k3aq6I: "x15481yp x12xz5k6",
    $$css: true
  }
};
const labelWrapperSizeStyles = {
  sm: {
    kAzted: "xisnujt",
    $$css: true
  },
  md: {
    kAzted: "xjwf9q1",
    $$css: true
  }
};
const styles = {
  container: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    $$css: true
  },
  containerSpread: {
    kjj79g: "x1qughib",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  statusGap: {
    keoZOQ: "xtbrsbv",
    $$css: true
  },
  switchWrapper: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kmuXW: "x2lah0s",
    kHBbk8: "xc8icb0",
    $$css: true
  },
  input: {
    kVAEAm: "x10l6tqk",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kSiTet: "xg01cxk",
    kkrTdU: "x1ypdohk",
    kY2c9j: "x1vjfegm",
    k7Eaqz: "xkagaj0",
    kAzted: "x80b3aj",
    k87sOh: "xijlfn0",
    kLqNvP: "x1hsn8va",
    k3aq6I: "x8i4i9p",
    $$css: true
  },
  inputDisabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  inputBusy: {
    kfzvcC: "x47corl",
    $$css: true
  },
  track: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kaIpWk: "xjspbzw",
    k1ekBW: "x15406qy",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    kB7OPa: "x9f619",
    kMzoRj: "xc342km x13rsnn2",
    ksu8eU: "xng3xce xx61lck",
    kVAM5u: "x1w1tqly",
    $$css: true
  },
  trackFocus: {
    kI3sdo: "x1a2a7pz xafswnp",
    kInvED: "xwz5zln",
    $$css: true
  },
  trackOff: {
    kWkggS: "xspzpui x1a8ae7b x9yvj25",
    $$css: true
  },
  trackOn: {
    kWkggS: "x1ewilqj xcgwt1n x1jzqe4",
    $$css: true
  },
  trackDisabled: {
    kSiTet: "xbyyjgo",
    kVAM5u: "xmnqt3w",
    $$css: true
  },
  trackDisabledOff: {
    kWkggS: "xspzpui",
    $$css: true
  },
  thumb: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kaIpWk: "xjspbzw",
    k1ekBW: "xh3q6i0",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  thumbOff: {
    kWkggS: "x10xzikg xwvh9j7",
    $$css: true
  },
  thumbOn: {
    kWkggS: "x10xzikg x1sir5s4",
    $$css: true
  },
  labelWrapper: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1lsbc85",
    kjj79g: "xl56j7k",
    $$css: true
  }
};
// Dynamic field width (number -> px, string used as-is).
const dynamicWidthStyles = {
  width: width => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }]
};

/**
 * A toggle switch component for boolean values.
 *
 * @example
 * ```
 * <Switch
 *   label="Enable notifications"
 *   value={enabled}
 *   onChange={setEnabled}
 * />
 * <Switch
 *   label="Dark mode"
 *   description="Switch to a darker color scheme"
 *   value={darkMode}
 *   onChange={setDarkMode}
 * />
 * ```
 */
export function Switch({
  label,
  isLabelHidden = false,
  description,
  onChange,
  changeAction,
  isLoading = false,
  value,
  isDisabled = false,
  htmlName,
  disabledMessage,
  isOptional = false,
  isRequired = false,
  onFocus,
  onBlur,
  labelIcon,
  labelTooltip,
  labelPosition = 'end',
  labelSpacing = 'hug',
  status,
  size = 'md',
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const id = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;
  const isOn = optimisticValue === true;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the switch row (which already exists) and the
  // native checkbox stays perceivable via aria-disabled instead of the disabled
  // attribute. Toggling is blocked by the isDisabled guard in onChange.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container row is not naturally focusable; focusin bubbles up from the
    // native input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });

  // Build aria-describedby from description and status message
  // Only include descriptionID when the element actually renders.
  // FieldLabel renders the description (with descriptionID) even when the
  // label is visually hidden — it's sr-only, so keep it linked.
  const describedByParts = [];
  if (description) {
    describedByParts.push(descriptionID);
  }
  if (status?.message) {
    describedByParts.push(statusMessageID);
  }
  if (showsDisabledMessage) {
    describedByParts.push(disabledMessageTooltip.describedBy);
  }
  const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
  const switchElement = /*#__PURE__*/_jsxs("div", {
    ...stylex.props(styles.switchWrapper, wrapperSizeStyles[size]),
    children: [/*#__PURE__*/_jsx("input", {
      ref: mergeRefs(ref, disabledMessageTooltip.positionRef),
      id: id,
      type: "checkbox",
      role: "switch"
      // Withhold the name while disabled: with a disabledMessage the
      // input stays focusable (not natively disabled), and a disabled
      // control must not submit.
      ,
      name: isDisabled ? undefined : htmlName,
      checked: isOn
      // With a disabledMessage the switch keeps focusability via aria-disabled
      // so the reason is focus-discoverable; toggling is still blocked by the
      // isDisabled guard in onChange below.
      ,
      disabled: isDisabled && !showsDisabledMessage,
      "aria-disabled": showsDisabledMessage ? 'true' : undefined,
      form: showsDisabledMessage ? '' : undefined,
      required: isRequired,
      onChange: e => {
        if (isDisabled || isBusy) {
          return;
        }
        const checked = e.target.checked;
        onChange?.(checked, e);
        if (changeAction && !e.defaultPrevented) {
          startTransition(async () => {
            setOptimisticValue(checked);
            await changeAction(checked, e);
          });
        }
      },
      onFocus: onFocus,
      onBlur: onBlur,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": status?.type === 'error' ? true : undefined,
      "aria-busy": isBusy || undefined,
      ...stylex.props(styles.input, inputSizeStyles[size], isDisabled && styles.inputDisabled, isBusy && styles.inputBusy)
    }), /*#__PURE__*/_jsx("div", {
      "aria-hidden": "true",
      ...mergeProps(themeProps('switch', {
        checked: isOn ? 'checked' : null,
        disabled: isDisabled ? 'disabled' : null,
        size
      }), stylex.props(styles.track, trackSizeStyles[size], isOn ? styles.trackOn : styles.trackOff, !isDisabled && styles.trackFocus, isDisabled && styles.trackDisabled, isDisabled && !isOn && styles.trackDisabledOff)),
      children: /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('switch-thumb', {
          checked: isOn ? 'checked' : null,
          size
        }), stylex.props(styles.thumb, isOn ? thumbOnSizeStyles[size] : thumbOffSizeStyles[size], isOn ? styles.thumbOn : styles.thumbOff)),
        children: isBusy && /*#__PURE__*/_jsx(Spinner, {
          size: "sm"
        })
      })
    }), isBusy && /*#__PURE__*/_jsx(VisuallyHidden, {
      role: "status",
      children: "Loading"
    })]
  });
  const labelElement = /*#__PURE__*/_jsx("div", {
    ...stylex.props(styles.labelWrapper, labelWrapperSizeStyles[size]),
    children: /*#__PURE__*/_jsx(FieldLabel, {
      label: label,
      inputID: id,
      isLabelHidden: isLabelHidden,
      isDisabled: isDisabled,
      isOptional: isOptional,
      isRequired: isRequired,
      labelIcon: labelIcon,
      labelTooltip: labelTooltip,
      description: description,
      descriptionID: descriptionID
    })
  });
  return /*#__PURE__*/_jsxs("div", {
    ...mergeProps(themeProps('switch-field', {
      labelPosition: labelPosition !== 'end' ? labelPosition : undefined,
      labelSpacing: labelSpacing !== 'hug' ? labelSpacing : undefined
    }), stylex.props(width != null && dynamicWidthStyles.width(width), xstyle), className, style),
    ...rest,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        // Interaction (hover/focus) listeners for the disabled-message
        // tooltip attach to the whole row for a larger trigger target;
        // positioning anchors on the switch itself (above) so the tooltip
        // appears next to the control, not the far edge of the row.
        // Handlers are gated internally by isEnabled, so attaching
        // unconditionally is safe.
        disabledMessageTooltip.interactionRef(el);
      },
      ...stylex.props(styles.container, labelSpacing === 'spread' && styles.containerSpread, !isDisabled && switchScope),
      children: [' ', labelPosition === 'start' ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [labelElement, switchElement]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [switchElement, labelElement]
      })]
    }), status?.message && /*#__PURE__*/_jsx(FieldStatus, {
      type: status.type,
      message: status.message,
      id: statusMessageID,
      variant: "detached",
      xstyle: styles.statusGap
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
Switch.displayName = 'Switch';